import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { BeforeAfterSlider } from "@/components/site/BeforeAfter";
import lobbyBefore from "@/assets/lobby-before.jpg.asset.json";
import lobbyAfter from "@/assets/lobby-after.jpg.asset.json";
import case2Before from "@/assets/case2-before.jpg.asset.json";
import case2After from "@/assets/case2-after.jpg.asset.json";
import case3Before from "@/assets/case3-before.jpg.asset.json";
import case3After from "@/assets/case3-after.jpg.asset.json";
import case4Before from "@/assets/case4-before.jpg.asset.json";
import case4After from "@/assets/case4-after.jpg.asset.json";

type Case = {
  title: string;
  location?: string;
  description: string;
  before: string;
  after: string;
};

// Add new cases here as photos come in.
const cases: Case[] = [
  {
    title: "Commercial Lobby Floor Installation",
    location: "Utah",
    description:
      "A commercial lobby transformed from bare uncoupling membrane and mortar during prep into a polished, large-format stone-look tile floor. The finished corridor features clean grout lines, coordinated baseboard trim, and a durable surface built to handle high foot traffic.",
    before: lobbyBefore.url,
    after: lobbyAfter.url,
  },
  {
    title: "Break Room Floor & Wet-Area Prep",
    location: "Utah",
    description:
      "From bare studs, waterproofing, and a rough substrate to a bright, finished break room with large-format marble-look porcelain tile. The RKD crew handled demolition, waterproof membrane, plumbing rough-in coordination, and a precision tile layout for a durable, easy-to-clean commercial space.",
    before: case2Before.url,
    after: case2After.url,
  },
  {
    title: "Commercial Lobby Entry Transformation",
    location: "Utah",
    description:
      "A rough, spalled concrete slab at the main entry became a bright, high-end commercial lobby. RKD ground and prepped the substrate, waterproofed transitions, and installed large-format marble-look porcelain with a recessed walk-off mat inlay for a clean, durable first impression.",
    before: case3Before.url,
    after: case3After.url,
  },
  {
    title: "Commercial Restroom Build-Out",
    location: "Utah",
    description:
      "A gutted restroom shell with exposed plumbing rough-ins and waterproofed walls became a clean, code-compliant commercial restroom. RKD installed dark large-format wall tile with metal trim edges, coordinated partition-wall transitions, and a light textured porcelain floor for a low-maintenance, high-traffic finish.",
    before: case4Before.url,
    after: case4After.url,
  },
];

export const Route = createFileRoute("/before-after")({
  component: BeforeAfterPage,
  head: () => ({
    meta: [
      { title: "Before & After — RKD Tile Transformations" },
      {
        name: "description",
        content:
          "See the transformation. Interactive before-and-after sliders of real RKD Tile bathroom, kitchen, flooring, and shower projects across Utah.",
      },
      { property: "og:title", content: "Before & After — RKD Tile" },
      { property: "og:url", content: "https://rkdtile.com/before-after" },
      {
        property: "og:description",
        content:
          "Drag the slider to see real RKD Tile transformations — bathrooms, kitchens, flooring, and more.",
      },
    ],
    links: [{ rel: "canonical", href: "https://rkdtile.com/before-after" }],
  }),
});

function BeforeAfterPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-ink text-cream">
          <div className="container-prose py-20 md:py-28">
            <span className="text-xs tracking-[0.25em] uppercase text-accent font-semibold">
              Real Projects · Real Transformations
            </span>
            <h1 className="mt-4 font-display text-4xl md:text-6xl font-semibold leading-[1.05]">
              Before <em className="not-italic text-accent">&</em> After
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-cream/80">
              Drag the slider on each case to reveal the transformation. Every project below was
              designed, prepped, and installed by the RKD Tile crew.
            </p>
          </div>
        </section>

        {/* Cases */}
        <section className="py-16 md:py-24">
          <div className="container-prose space-y-20 md:space-y-28">
            {cases.map((c, i) => (
              <article key={i} className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <BeforeAfterSlider before={c.before} after={c.after} alt={c.title} />
                </div>
                <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-accent font-semibold">
                    Case {String(i + 1).padStart(2, "0")}
                    {c.location ? ` · ${c.location}` : ""}
                  </div>
                  <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-foreground leading-tight">
                    {c.title}
                  </h2>
                  <p className="mt-4 text-foreground/90 leading-relaxed">{c.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
    </div>
  );
}